import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Star, UserPlus, UserMinus, ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { AppointMemberModal } from './appoint-member-modal'
import {
  useCommitteeAppointments,
  useCommitteeHistory,
  useEndAppointment,
} from '@/hooks/use-committee'
import { useMembersList } from '@/hooks/use-members'
import { COMMITTEE_POSITION_LABELS } from '@riverpark/types'
import type { CommitteePosition } from '@riverpark/types'

const POSITION_ORDER: CommitteePosition[] = [
  'chairman',
  'vice_chairman',
  'treasurer',
  'secretary',
  'committee_member',
]

function formatDate(ts: number) {
  return new Date(ts * 1000).toLocaleDateString('en-KE', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

interface CommitteeRosterProps {
  memberDetailBasePath: string
}

export function CommitteeRoster({ memberDetailBasePath }: CommitteeRosterProps) {
  const [appointOpen, setAppointOpen] = useState(false)
  const [preselectedMemberId, setPreselectedMemberId] = useState<string | null>(null)
  const [historyOpen, setHistoryOpen] = useState(false)

  const { data: appointmentsData, isLoading: loadingAppts } = useCommitteeAppointments()
  const { data: historyData } = useCommitteeHistory()
  const { data: membersData, isLoading: loadingMembers } = useMembersList({
    page: 1,
    limit: 50,
    role: 'committee',
  })
  const endAppointment = useEndAppointment()

  const appointments = appointmentsData?.data ?? []
  const committeeMembers = membersData?.data ?? []

  // Build a map of memberId → appointment for quick lookup
  const apptByMember = new Map(appointments.map((a) => [a.memberId, a]))

  // Sort appointments by position order
  const sortedAppointments = [...appointments].sort(
    (a, b) => POSITION_ORDER.indexOf(a.position) - POSITION_ORDER.indexOf(b.position),
  )

  // Members without an active appointment
  const unassigned = committeeMembers.filter((m) => !apptByMember.has(m.id))

  const preselectedMember = preselectedMemberId
    ? (committeeMembers.find((m) => m.id === preselectedMemberId) ?? null)
    : null

  async function handleEndTerm(appointmentId: string, memberName: string) {
    if (!confirm(`End ${memberName}'s committee position?`)) return
    await endAppointment.mutateAsync(appointmentId)
  }

  const isLoading = loadingAppts || loadingMembers

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <p className="font-sans text-sm text-muted-foreground">
            {appointments.length} active position{appointments.length !== 1 ? 's' : ''} ·{' '}
            {committeeMembers.length} committee member{committeeMembers.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button
          size="sm"
          onClick={() => {
            setPreselectedMemberId(null)
            setAppointOpen(true)
          }}
        >
          <UserPlus size={14} strokeWidth={1.75} className="mr-1.5" />
          Appoint member
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-16 animate-pulse rounded border border-border-2 bg-muted/40"
            />
          ))}
        </div>
      )}

      {/* Active appointments */}
      {!isLoading && sortedAppointments.length > 0 && (
        <div className="space-y-2">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-muted-foreground">
            Current appointments
          </p>
          <div className="divide-y divide-border-2 overflow-hidden rounded border border-border-2">
            {sortedAppointments.map((appt) => (
              <div
                key={appt.id}
                className="flex items-center justify-between gap-4 bg-card px-4 py-3 transition-colors hover:bg-cream-2/30"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <Star size={14} className="shrink-0 text-amber-500" strokeWidth={1.75} />
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-baseline gap-2">
                      <Link
                        to={`${memberDetailBasePath}/${appt.memberId}`}
                        className="font-sans text-sm font-medium text-ink transition-colors hover:text-primary"
                      >
                        {appt.memberName}
                      </Link>
                      <span className="font-sans text-xs text-muted-foreground">
                        {COMMITTEE_POSITION_LABELS[appt.position]}
                      </span>
                    </div>
                    <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                      Since {formatDate(appt.startedAt)}
                      {appt.appointedByName && ` · Appointed by ${appt.appointedByName}`}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setPreselectedMemberId(appt.memberId)
                      setAppointOpen(true)
                    }}
                  >
                    Change
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-status-overdue-text hover:bg-status-overdue-text/10"
                    onClick={() => void handleEndTerm(appt.id, appt.memberName)}
                    disabled={endAppointment.isPending}
                  >
                    <UserMinus size={13} strokeWidth={1.75} className="mr-1" />
                    End term
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unassigned committee members */}
      {!isLoading && unassigned.length > 0 && (
        <div className="space-y-2">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-muted-foreground">
            No position assigned
          </p>
          <div className="divide-y divide-border-2 overflow-hidden rounded border border-border-2">
            {unassigned.map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between gap-4 bg-card px-4 py-3 transition-colors hover:bg-cream-2/30"
              >
                <Link
                  to={`${memberDetailBasePath}/${member.id}`}
                  className="font-sans text-sm font-medium text-ink transition-colors hover:text-primary"
                >
                  {member.name}
                </Link>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPreselectedMemberId(member.id)
                    setAppointOpen(true)
                  }}
                >
                  <UserPlus size={13} strokeWidth={1.75} className="mr-1" />
                  Appoint
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isLoading && committeeMembers.length === 0 && (
        <p className="py-8 text-center font-sans text-sm text-muted-foreground">
          No committee members found. Invite members and assign them the Committee role first.
        </p>
      )}

      {/* Past appointments */}
      {(() => {
        const past = (historyData?.data ?? []).filter((a) => a.endedAt !== null)
        if (past.length === 0) return null
        return (
          <div className="space-y-2">
            <button
              onClick={() => setHistoryOpen((o) => !o)}
              className="flex w-full items-center justify-between font-sans text-xs font-medium uppercase tracking-widest text-muted-foreground transition-colors hover:text-ink"
            >
              <span>Past appointments ({past.length})</span>
              {historyOpen ? (
                <ChevronUp size={14} strokeWidth={1.75} />
              ) : (
                <ChevronDown size={14} strokeWidth={1.75} />
              )}
            </button>

            {historyOpen && (
              <div className="divide-y divide-border-2 overflow-hidden rounded border border-border-2">
                {past.map((appt) => (
                  <div
                    key={appt.id}
                    className="flex items-center justify-between gap-4 bg-card px-4 py-3"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-baseline gap-2">
                        <Link
                          to={`${memberDetailBasePath}/${appt.memberId}`}
                          className="font-sans text-sm font-medium text-ink transition-colors hover:text-primary"
                        >
                          {appt.memberName}
                        </Link>
                        <span className="font-sans text-xs text-muted-foreground">
                          {COMMITTEE_POSITION_LABELS[appt.position]}
                        </span>
                      </div>
                      <p className="mt-0.5 font-sans text-xs text-muted-foreground">
                        {formatDate(appt.startedAt)} →{' '}
                        {appt.endedAt ? formatDate(appt.endedAt) : ''}
                        {appt.appointedByName && ` · Appointed by ${appt.appointedByName}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )
      })()}

      <AppointMemberModal
        open={appointOpen}
        onClose={() => {
          setAppointOpen(false)
          setPreselectedMemberId(null)
        }}
        preselectedMember={preselectedMember}
        committeeMembers={committeeMembers.map((m) => ({ id: m.id, name: m.name }))}
      />
    </div>
  )
}
